interface Particle {
	x: number;
	y: number;
	vx: number;
	vy: number;
	life: number;
	maxLife: number;
	size: number;
	growthRate: number;
	rotation: number;
	rotationSpeed: number;
	color: { r: number; g: number; b: number };
	turbulence: number;
}

export class VisualRenderingAgent {
	// ===== PRIVATE STATE =====
	private canvas: HTMLCanvasElement;
	private ctx: CanvasRenderingContext2D;
	private particles: Particle[] = [];
	private audioAmplitude = 0;
	private time = 0;
	private isPlaying = false;
	private animationFrameId: number | null = null;
	private onToggle: (() => void) | undefined;
	private spawnTimer = 0;

	// ===== CONSTRUCTOR =====
	constructor(canvas: HTMLCanvasElement, onToggle?: () => void) {
		this.canvas = canvas;
		const ctx = canvas.getContext("2d");
		if (!ctx) throw new Error("Could not get 2d context");
		this.ctx = ctx;
		this.onToggle = onToggle;

		this.setupCanvas();
		this.attachEventListeners();
		this.start();
	}

	// ===== PUBLIC INTERFACE =====
	updateAudioData(data: Uint8Array | null) {
		if (!data || !this.isPlaying) {
			this.audioAmplitude = 0;
			return;
		}

		// Calculate average amplitude
		let sum = 0;
		for (let i = 0; i < data.length; i++) {
			sum += data[i];
		}
		const average = sum / data.length;

		// Normalize and smooth
		const normalized = average / 255;
		this.audioAmplitude = this.audioAmplitude * 0.7 + normalized * 0.3;
	}

	setPlaying(playing: boolean) {
		this.isPlaying = playing;
	}

	start() {
		if (this.animationFrameId) return;
		this.animate();
	}

	stop() {
		if (this.animationFrameId) {
			cancelAnimationFrame(this.animationFrameId);
			this.animationFrameId = null;
		}
	}

	destroy() {
		this.stop();
		this.canvas.removeEventListener("click", this.handleClick);
		window.removeEventListener("resize", this.handleResize);
	}

	// ===== PRIVATE METHODS =====
	private setupCanvas() {
		this.handleResize();
	}

	private attachEventListeners() {
		this.canvas.addEventListener("click", this.handleClick);
		window.addEventListener("resize", this.handleResize);
	}

	private handleClick = () => {
		if (this.onToggle) {
			this.onToggle();
		}
	};

	private handleResize = () => {
		this.canvas.width = window.innerWidth;
		this.canvas.height = window.innerHeight;
	};

	private noise(x: number, y: number): number {
		return Math.sin(x * 0.01) * Math.cos(y * 0.01) * 0.5 + 0.5;
	}

	private createParticle(): Particle {
		const colorBoost = this.audioAmplitude * 30;
		const baseColor = {
			r: 130 + Math.random() * 50 + colorBoost,
			g: 110 + Math.random() * 40 + colorBoost * 0.8,
			b: 50 + Math.random() * 50 + colorBoost * 0.5,
		};

		const maxLife = 120 + Math.random() * 120;
		const velocityBoost = this.audioAmplitude * 1.5;
		const minSize = 20 + this.audioAmplitude * 40;
		const sizeVariation = 10 + this.audioAmplitude * 70;
		const particleSize = minSize + Math.random() * sizeVariation;

		return {
			x: this.canvas.width / 2 + (Math.random() - 0.5) * 60,
			y: this.canvas.height - 120,
			vx: (Math.random() - 0.5) * (2.5 + velocityBoost),
			vy: -(2.5 + velocityBoost) - Math.random() * 2,
			life: maxLife,
			maxLife,
			size: particleSize,
			growthRate: 0.8 + Math.random() * 1.2 + this.audioAmplitude * 0.5,
			rotation: Math.random() * Math.PI * 2,
			rotationSpeed: (Math.random() - 0.5) * 0.02,
			color: baseColor,
			turbulence: Math.random() * 3 + this.audioAmplitude * 2,
		};
	}

	private drawButtCheeks() {
		const centerX = this.canvas.width / 2;
		const bottomY = this.canvas.height;
		const cheekWidth = 140;
		const cheekHeight = 160;
		const cheekSpacing = 50;

		// Draw both cheeks with smooth bottom edge
		this.ctx.save();
		this.ctx.beginPath();

		// Calculate edges
		const leftEdge = centerX - cheekSpacing - cheekWidth;
		const rightEdge = centerX + cheekSpacing + cheekWidth;

		// Start from bottom left
		this.ctx.moveTo(leftEdge, bottomY);

		// Left cheek arc (top half)
		this.ctx.ellipse(
			centerX - cheekSpacing,
			bottomY - 10,
			cheekWidth,
			cheekHeight,
			0,
			Math.PI,
			0,
			false,
		);

		// Right cheek arc (top half)
		this.ctx.ellipse(
			centerX + cheekSpacing,
			bottomY - 10,
			cheekWidth,
			cheekHeight,
			0,
			Math.PI,
			0,
			false,
		);

		// Close with straight line across bottom
		this.ctx.lineTo(rightEdge, bottomY);
		this.ctx.lineTo(leftEdge, bottomY);
		this.ctx.closePath();

		// Create a compound gradient for both cheeks
		const overallGradient = this.ctx.createLinearGradient(
			centerX - cheekSpacing - cheekWidth,
			bottomY - cheekHeight * 0.5,
			centerX + cheekSpacing + cheekWidth,
			bottomY - cheekHeight * 0.5,
		);
		overallGradient.addColorStop(0, "rgba(200, 160, 140, 1)");
		overallGradient.addColorStop(0.25, "rgba(255, 220, 200, 1)");
		overallGradient.addColorStop(0.5, "rgba(240, 200, 180, 1)");
		overallGradient.addColorStop(0.75, "rgba(255, 220, 200, 1)");
		overallGradient.addColorStop(1, "rgba(200, 160, 140, 1)");

		this.ctx.fillStyle = overallGradient;
		this.ctx.fill();
		this.ctx.restore();

		// Add individual highlights on each cheek
		// Left highlight
		this.ctx.save();
		this.ctx.beginPath();
		this.ctx.arc(
			centerX - cheekSpacing - 30,
			bottomY - cheekHeight * 0.5,
			60,
			0,
			Math.PI * 2,
		);
		const leftHighlight = this.ctx.createRadialGradient(
			centerX - cheekSpacing - 30,
			bottomY - cheekHeight * 0.5,
			0,
			centerX - cheekSpacing - 30,
			bottomY - cheekHeight * 0.5,
			60,
		);
		leftHighlight.addColorStop(0, "rgba(255, 240, 230, 0.6)");
		leftHighlight.addColorStop(1, "rgba(255, 240, 230, 0)");
		this.ctx.fillStyle = leftHighlight;
		this.ctx.fill();
		this.ctx.restore();

		// Right highlight
		this.ctx.save();
		this.ctx.beginPath();
		this.ctx.arc(
			centerX + cheekSpacing + 30,
			bottomY - cheekHeight * 0.5,
			60,
			0,
			Math.PI * 2,
		);
		const rightHighlight = this.ctx.createRadialGradient(
			centerX + cheekSpacing + 30,
			bottomY - cheekHeight * 0.5,
			0,
			centerX + cheekSpacing + 30,
			bottomY - cheekHeight * 0.5,
			60,
		);
		rightHighlight.addColorStop(0, "rgba(255, 240, 230, 0.6)");
		rightHighlight.addColorStop(1, "rgba(255, 240, 230, 0)");
		this.ctx.fillStyle = rightHighlight;
		this.ctx.fill();
		this.ctx.restore();

		// Add shadow in the middle (the crack) - darker at bottom, fades at top
		this.ctx.save();

		// Create a temporary canvas for the shadow effect
		const shadowWidth = 60;
		const tempCanvas = document.createElement("canvas");
		tempCanvas.width = shadowWidth;
		tempCanvas.height = cheekHeight;
		const tempCtx = tempCanvas.getContext("2d");

		if (tempCtx) {
			// Draw vertical gradient (top to bottom fade)
			const vertGrad = tempCtx.createLinearGradient(
				shadowWidth / 2,
				0,
				shadowWidth / 2,
				cheekHeight,
			);
			vertGrad.addColorStop(0, "rgba(0, 0, 0, 0)");
			vertGrad.addColorStop(0.3, "rgba(0, 0, 0, 0.1)");
			vertGrad.addColorStop(0.6, "rgba(0, 0, 0, 0.35)");
			vertGrad.addColorStop(1, "rgba(0, 0, 0, 0.6)");

			tempCtx.fillStyle = vertGrad;
			tempCtx.fillRect(0, 0, shadowWidth, cheekHeight);

			// Apply horizontal gradient mask
			tempCtx.globalCompositeOperation = "destination-in";
			const horizGrad = tempCtx.createLinearGradient(0, 0, shadowWidth, 0);
			horizGrad.addColorStop(0, "rgba(0, 0, 0, 0)");
			horizGrad.addColorStop(0.5, "rgba(0, 0, 0, 1)");
			horizGrad.addColorStop(1, "rgba(0, 0, 0, 0)");

			tempCtx.fillStyle = horizGrad;
			tempCtx.fillRect(0, 0, shadowWidth, cheekHeight);

			// Draw to main canvas
			this.ctx.drawImage(
				tempCanvas,
				centerX - shadowWidth / 2,
				bottomY - cheekHeight,
			);
		}

		this.ctx.restore();

		// Draw the chocolate starfish (blended with butt color) with opening/closing animation
		this.ctx.save();
		const starfishRadius = 25;
		const starfishY = bottomY - 100;
		const points = 6;

		// Audio-reactive pulsing - combines audio amplitude with subtle breathing
		const breathe = Math.sin(this.time * 0.5) * 0.15 + 0.15; // Subtle breathing 0 to 0.3
		const pulse = this.audioAmplitude * 0.7 + breathe; // Primarily driven by audio

		// Outer starfish shape (static)
		this.ctx.beginPath();
		for (let i = 0; i < points * 2; i++) {
			const angle = (i * Math.PI) / points;
			const baseRadius = i % 2 === 0 ? starfishRadius : starfishRadius * 0.5;
			const x = centerX + Math.cos(angle) * baseRadius;
			const y = starfishY + Math.sin(angle) * baseRadius;

			if (i === 0) {
				this.ctx.moveTo(x, y);
			} else {
				this.ctx.lineTo(x, y);
			}
		}
		this.ctx.closePath();

		// Peachy-brown gradient that blends with butt cheeks
		const starfishGradient = this.ctx.createRadialGradient(
			centerX,
			starfishY,
			0,
			centerX,
			starfishY,
			starfishRadius,
		);
		starfishGradient.addColorStop(0, "rgba(140, 100, 80, 1)");
		starfishGradient.addColorStop(0.5, "rgba(160, 120, 90, 1)");
		starfishGradient.addColorStop(1, "rgba(180, 140, 110, 0.8)");

		this.ctx.fillStyle = starfishGradient;
		this.ctx.fill();

		// Add subtle texture with wrinkles (static)
		this.ctx.strokeStyle = "rgba(120, 90, 70, 0.4)";
		this.ctx.lineWidth = 1;
		for (let i = 0; i < points; i++) {
			const angle = (i * 2 * Math.PI) / points;
			this.ctx.beginPath();
			this.ctx.moveTo(centerX, starfishY);
			this.ctx.lineTo(
				centerX + Math.cos(angle) * starfishRadius * 0.8,
				starfishY + Math.sin(angle) * starfishRadius * 0.8,
			);
			this.ctx.stroke();
		}

		// Inner darker center that opens/closes (only this pulses)
		const centerSize = starfishRadius * (0.05 + pulse * 0.15); // Opens wider when pulsing
		this.ctx.beginPath();
		this.ctx.arc(centerX, starfishY, centerSize, 0, Math.PI * 2);
		this.ctx.fillStyle = "rgba(100, 70, 50, 0.7)";
		this.ctx.fill();

		this.ctx.restore();
	}

	private animate = () => {
		this.time += 0.016;
		this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

		this.drawButtCheeks();

		// Particle spawning
		const hasSound = this.audioAmplitude > 0.05;
		const baseSpawnRate = 4;
		const maxParticles = 80;

		if (this.isPlaying && hasSound) {
			this.spawnTimer++;
			const dynamicSpawnRate = Math.max(
				1,
				baseSpawnRate - Math.floor(this.audioAmplitude * 3),
			);
			const shouldSpawnMultiple =
				this.audioAmplitude > 0.6 && Math.random() < this.audioAmplitude;

			if (
				this.spawnTimer >= dynamicSpawnRate &&
				this.particles.length < maxParticles
			) {
				this.particles.push(this.createParticle());
				if (
					shouldSpawnMultiple &&
					this.particles.length < maxParticles
				) {
					this.particles.push(this.createParticle());
				}
				this.spawnTimer = 0;
			}
		}

		// Draw smoke particles
		for (let i = this.particles.length - 1; i >= 0; i--) {
			const p = this.particles[i];

			// Apply noise-based turbulence for organic movement
			const noiseX = this.noise(p.x * 0.01 + this.time, p.y * 0.01);
			const noiseY = this.noise(p.x * 0.01, p.y * 0.01 + this.time);

			p.vx += (noiseX - 0.5) * p.turbulence * 0.25;
			p.vy += (noiseY - 0.5) * p.turbulence * 0.12;

			// Add buoyancy (smoke rises faster as it gets lighter)
			const lifeRatio = p.life / p.maxLife;
			p.vy -= 0.04 * (1 - lifeRatio);

			// Update position
			p.x += p.vx;
			p.y += p.vy;

			// Air resistance
			p.vx *= 0.99;
			p.vy *= 0.99;

			// Rotation
			p.rotation += p.rotationSpeed;

			// Grow and fade
			p.size += p.growthRate;
			p.growthRate *= 0.99; // Slow down growth over time
			p.life--;

			// Calculate opacity with smooth fade in and out
			let opacity = p.life / p.maxLife;
			if (lifeRatio > 0.9) {
				// Fade in
				opacity = (1 - lifeRatio) * 10;
			}
			opacity = Math.max(0, Math.min(1, opacity));

			// Save context for rotation
			this.ctx.save();
			this.ctx.translate(p.x, p.y);
			this.ctx.rotate(p.rotation);

			// Draw particle with more complex gradient
			const gradient = this.ctx.createRadialGradient(0, 0, 0, 0, 0, p.size);

			// Create multi-stop gradient for more realistic smoke
			gradient.addColorStop(
				0,
				`rgba(${p.color.r}, ${p.color.g}, ${p.color.b}, ${opacity * 0.7})`,
			);
			gradient.addColorStop(
				0.3,
				`rgba(${p.color.r - 10}, ${p.color.g - 10}, ${p.color.b - 5}, ${opacity * 0.5})`,
			);
			gradient.addColorStop(
				0.6,
				`rgba(${p.color.r - 25}, ${p.color.g - 25}, ${p.color.b - 15}, ${opacity * 0.25})`,
			);
			gradient.addColorStop(
				0.85,
				`rgba(${p.color.r - 40}, ${p.color.g - 40}, ${p.color.b - 25}, ${opacity * 0.1})`,
			);
			gradient.addColorStop(1, "rgba(0, 0, 0, 0)");

			this.ctx.fillStyle = gradient;
			this.ctx.beginPath();

			// Draw irregular shape instead of perfect circle
			const shapePoints = 8;
			for (let j = 0; j < shapePoints; j++) {
				const angle = (j / shapePoints) * Math.PI * 2;
				const radius = p.size * (0.85 + Math.random() * 0.3);
				const x = Math.cos(angle) * radius;
				const y = Math.sin(angle) * radius;

				if (j === 0) {
					this.ctx.moveTo(x, y);
				} else {
					this.ctx.lineTo(x, y);
				}
			}
			this.ctx.closePath();
			this.ctx.fill();

			this.ctx.restore();

			// Remove dead particles
			if (p.life <= 0 || p.y < -p.size * 2) {
				this.particles.splice(i, 1);
			}
		}

		this.animationFrameId = requestAnimationFrame(this.animate);
	};
}
